'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Check, Copy, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { copyToClipboard } from '@/utils/clipboard';

interface Props {
  trigger: ReactNode;
  title: string;
  email: string;
}

export const UserModerationModal = ({ trigger, title, email }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(email);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <button onClick={toggleModal} className={`text-zinc-500 cursor-pointer hover:text-white`}>
        {trigger}
      </button>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
          >
            <span className="flex justify-between items-center p-4 border-zinc-700">
              <span className="text-xl font-semibold">{title}</span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span></span>
            <span className="p-4 flex flex-col gap-6">
              <span>
                <span className="flex justify-between items-center">
                  <span>{email}</span>
                  <button onClick={handleCopy} className={`flex items-center justify-center p-0`}>
                    {isCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" color={'#00FF00'} />
                        <span style={{ color: '#00FF00' }}>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </span>
              </span>
              <Button className={'w-full'} variant={'slim'} isPrimary={true} onClick={toggleModal}>
                Close
              </Button>
            </span>
          </span>
        </span>
      )}
    </>
  );
};

export default UserModerationModal;
